/**
 * Holidays admin — Phase 1A.
 * Read template + per-tenant lists, toggle `run_flag`, add custom dates.
 * (Bulk clone-from-template ships in 1B.)
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  CalendarBlank, ArrowLeft, Plus, Trash, Spinner, X, ToggleLeft, ToggleRight,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  seqListHolidays, seqCreateHoliday, seqPatchHoliday, seqDeleteHoliday,
} from "@/lib/sequencingApi";
import { useT } from "@/lib/i18n";

export default function SequencingHolidaysPage() {
  const t = useT();
  const [tenantId, setTenantId] = useState("__template__");
  const [year, setYear]         = useState(new Date().getFullYear());
  const [items, setItems]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [err, setErr]           = useState("");
  const [showCreate, setShowCreate] = useState(false);

  async function refresh() {
    setLoading(true); setErr("");
    try {
      const data = await seqListHolidays({ tenant_id: tenantId, year });
      setItems(data.items || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || String(e));
    } finally { setLoading(false); }
  }
  useEffect(() => { refresh(); }, [tenantId, year]);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="seq-holidays-page">
      <header className="sticky top-0 z-10 bg-white border-b border-slate-200 px-4 md:px-6 py-3 flex items-center gap-3">
        <Link to="/sequencing" data-testid="seq-holidays-back"
              className="w-8 h-8 inline-flex items-center justify-center rounded-sm hover:bg-slate-100 text-slate-500">
          <ArrowLeft size={16} weight="bold" />
        </Link>
        <CalendarBlank size={20} weight="fill" className="text-blue-700 shrink-0" />
        <h1 className="flex-1 text-base md:text-lg font-bold font-['Chivo'] text-slate-900 truncate">
          {t("seq.holidays_title", "Holiday calendar")}
        </h1>
        <button type="button" onClick={() => setShowCreate(true)} data-testid="seq-holidays-create-open"
                className="text-xs font-bold uppercase tracking-wider px-3 h-9 rounded-sm bg-blue-700 text-white hover:bg-blue-800 inline-flex items-center gap-1.5">
          <Plus size={12} weight="bold" /> {t("seq.holidays_new", "Add")}
        </button>
      </header>

      <main className="max-w-3xl mx-auto px-4 md:px-6 py-6 space-y-3">
        <div className="flex flex-wrap items-end gap-3 bg-white border border-slate-200 rounded-sm p-3">
          <label className="block flex-1 min-w-[180px]">
            <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-600 mb-1">Tenant</span>
            <input value={tenantId} onChange={e => setTenantId(e.target.value)}
                   data-testid="seq-holidays-filter-tenant"
                   placeholder="__template__ or CustomerID"
                   className="w-full px-3 py-2 text-sm border border-slate-300 rounded-sm font-mono focus:border-blue-500 focus:outline-none" />
          </label>
          <label className="block w-24">
            <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-600 mb-1">Year</span>
            <input value={year} type="number" onChange={e => setYear(Number(e.target.value) || year)}
                   data-testid="seq-holidays-filter-year"
                   className="w-full px-3 py-2 text-sm border border-slate-300 rounded-sm focus:border-blue-500 focus:outline-none" />
          </label>
        </div>

        {err && <div className="bg-red-50 border border-red-200 text-red-700 text-sm p-3 rounded-sm">{err}</div>}
        {loading ? (
          <div className="py-10 text-center"><Spinner size={20} className="mx-auto animate-spin text-slate-400" /></div>
        ) : items.length === 0 ? (
          <div className="bg-white border border-dashed border-slate-300 rounded-sm p-8 text-center text-slate-500">
            <CalendarBlank size={28} weight="fill" className="mx-auto text-slate-300 mb-2" />
            <p className="text-sm font-medium">{t("seq.holidays_empty", "No holidays for this tenant + year")}</p>
          </div>
        ) : (
          <ul className="space-y-2" data-testid="seq-holidays-list">
            {items.map(h => (
              <li key={h.id}
                  className="bg-white border border-slate-200 rounded-sm p-3 flex items-center gap-3"
                  data-testid={`seq-holidays-row-${h.id}`}>
                <div className="w-24 shrink-0">
                  <div className="text-sm font-mono font-bold text-slate-900">{h.date}</div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-400">{h.country || ""}</div>
                </div>
                <div className="flex-1 min-w-0 text-sm text-slate-700 truncate">{h.name}</div>
                <button type="button"
                        onClick={async () => {
                          try {
                            await seqPatchHoliday(h.id, { run_flag: !h.run_flag });
                            refresh();
                          } catch (e) { toast.error(e?.response?.data?.detail || e?.message); }
                        }}
                        data-testid={`seq-holidays-toggle-${h.id}`}
                        title="Toggle run_flag"
                        className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm border inline-flex items-center gap-1
                          ${h.run_flag
                            ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                            : "border-slate-300 bg-slate-50 text-slate-600"}`}>
                  {h.run_flag ? <ToggleRight size={12} weight="fill" /> : <ToggleLeft size={12} weight="fill" />}
                  {h.run_flag ? "run" : "off"}
                </button>
                <button type="button"
                        onClick={async () => {
                          if (!window.confirm(`Delete holiday "${h.name}"?`)) return;
                          try { await seqDeleteHoliday(h.id); refresh(); }
                          catch (e) { toast.error(e?.response?.data?.detail || e?.message); }
                        }}
                        data-testid={`seq-holidays-delete-${h.id}`}
                        className="w-8 h-8 inline-flex items-center justify-center rounded-sm text-slate-400 hover:text-red-700 hover:bg-red-50">
                  <Trash size={14} weight="bold" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </main>

      {showCreate && (
        <CreateHolidayModal defaultTenant={tenantId} defaultYear={year}
                            onClose={() => setShowCreate(false)}
                            onCreated={() => { setShowCreate(false); refresh(); }} />
      )}
    </div>
  );
}

function CreateHolidayModal({ defaultTenant, defaultYear, onClose, onCreated }) {
  const t = useT();
  const [tenantId, setTenantId] = useState(defaultTenant);
  const [year, setYear]         = useState(defaultYear);
  const [date, setDate]         = useState("");
  const [name, setName]         = useState("");
  const [country, setCountry]   = useState("CA");
  const [runFlag, setRunFlag]   = useState(false);
  const [busy, setBusy]         = useState(false);

  async function submit(e) {
    e?.preventDefault?.();
    if (!date || !name.trim()) return;
    setBusy(true);
    try {
      await seqCreateHoliday({
        tenant_id: tenantId, year: Number(year), date, name: name.trim(),
        country, run_flag: runFlag,
      });
      toast.success(t("seq.holidays_created", "Holiday added"));
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || String(e));
    } finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 z-30 bg-black/50 flex items-end sm:items-center justify-center p-3"
         data-testid="seq-holidays-create-modal">
      <form onSubmit={submit} className="bg-white rounded-sm w-full max-w-md p-5 space-y-3">
        <div className="flex items-center gap-2">
          <h2 className="text-base font-bold font-['Chivo'] text-slate-900 flex-1">{t("seq.holidays_new", "Add holiday")}</h2>
          <button type="button" onClick={onClose} data-testid="seq-holidays-create-close"
                  className="w-7 h-7 inline-flex items-center justify-center rounded-sm hover:bg-slate-100 text-slate-400">
            <X size={14} weight="bold" />
          </button>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-600 mb-1">Tenant</span>
            <input value={tenantId} onChange={e => setTenantId(e.target.value)} required
                   data-testid="seq-holidays-field-tenant"
                   className="w-full px-3 py-2 text-sm border border-slate-300 rounded-sm font-mono focus:border-blue-500 focus:outline-none" />
          </label>
          <label className="block">
            <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-600 mb-1">Year</span>
            <input value={year} type="number" onChange={e => setYear(Number(e.target.value))} required
                   data-testid="seq-holidays-field-year"
                   className="w-full px-3 py-2 text-sm border border-slate-300 rounded-sm focus:border-blue-500 focus:outline-none" />
          </label>
        </div>
        <label className="block">
          <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-600 mb-1">Date</span>
          <input value={date} type="date" onChange={e => setDate(e.target.value)} required
                 data-testid="seq-holidays-field-date"
                 className="w-full px-3 py-2 text-sm border border-slate-300 rounded-sm focus:border-blue-500 focus:outline-none" />
        </label>
        <label className="block">
          <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-600 mb-1">Name</span>
          <input value={name} onChange={e => setName(e.target.value)} required
                 data-testid="seq-holidays-field-name"
                 className="w-full px-3 py-2 text-sm border border-slate-300 rounded-sm focus:border-blue-500 focus:outline-none" />
        </label>
        <div className="grid grid-cols-2 gap-3 items-end">
          <label className="block">
            <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-600 mb-1">Country</span>
            <select value={country} onChange={e => setCountry(e.target.value)}
                    data-testid="seq-holidays-field-country"
                    className="w-full px-3 py-2 text-sm border border-slate-300 rounded-sm bg-white focus:border-blue-500 focus:outline-none">
              <option value="CA">CA</option>
              <option value="US">US</option>
              <option value="CA,US">CA + US</option>
              <option value="">—</option>
            </select>
          </label>
          <label className="flex items-center gap-2 text-sm select-none pb-1 cursor-pointer">
            <input type="checkbox" checked={runFlag} onChange={e => setRunFlag(e.target.checked)}
                   data-testid="seq-holidays-field-runflag" />
            Run compressors on this day
          </label>
        </div>

        <div className="flex items-center justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} data-testid="seq-holidays-create-cancel"
                  className="text-xs font-bold uppercase tracking-wider px-3 h-9 rounded-sm border border-slate-300 text-slate-700 hover:bg-slate-50">
            {t("common.cancel", "Cancel")}
          </button>
          <button type="submit" disabled={busy || !date || !name.trim()}
                  data-testid="seq-holidays-create-submit"
                  className="text-xs font-bold uppercase tracking-wider px-4 h-9 rounded-sm bg-blue-700 text-white hover:bg-blue-800 disabled:opacity-50 inline-flex items-center gap-1.5">
            {busy ? <Spinner size={12} className="animate-spin" /> : <Plus size={12} weight="bold" />}
            {t("seq.add", "Add")}
          </button>
        </div>
      </form>
    </div>
  );
}
